import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Languages, Loader2, User, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", account_type: "individual", business_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register(form);
      navigate(user.role === "manager" ? "/onboarding" : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-center bg-primary text-white p-14">
        <Languages size={48} />
        <h1 className="font-head font-extrabold text-4xl mt-6 leading-tight">{t("appName")}</h1>
        <p className="text-white/80 text-lg mt-4 max-w-md">{t("heroSub")}</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h2 className="font-head font-bold text-2xl text-foreground">{t("createAccount")}</h2>
          <p className="text-muted-foreground mt-1 mb-6">{t("register")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "individual", icon: User, label: t("individual") },
                { v: "business", icon: Building2, label: t("business") },
              ].map(({ v, icon: Icon, label }) => (
                <button
                  type="button"
                  key={v}
                  onClick={() => setForm((f) => ({ ...f, account_type: v }))}
                  data-testid={`account-type-${v}`}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-colors ${
                    form.account_type === v ? "border-accent bg-accent/5 text-primary" : "border-border text-muted-foreground"
                  }`}
                >
                  <Icon size={20} /> <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>

            <div>
              <Label>{t("name")}</Label>
              <Input value={form.name} onChange={set("name")} required data-testid="register-name" className="h-12 mt-1.5" />
            </div>
            {form.account_type === "business" && (
              <div>
                <Label>{t("businessName")}</Label>
                <Input value={form.business_name} onChange={set("business_name")} data-testid="register-business-name" className="h-12 mt-1.5" />
              </div>
            )}
            <div>
              <Label>{t("email")}</Label>
              <Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email" className="h-12 mt-1.5" />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input type="password" value={form.password} onChange={set("password")} required data-testid="register-password" className="h-12 mt-1.5" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-base">
              {loading ? <Loader2 className="animate-spin" /> : t("register")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t("haveAccount")} <Link to="/login" className="text-accent font-semibold" data-testid="to-login">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
